/**
 * 天赋测试进度存储工具
 * 使用 localStorage 存储测试进度，支持7天过期自动清理
 */

const STORAGE_KEY = 'talent_test_progress'
const RESULTS_STORAGE_KEY = 'talent_test_results'
const EXPIRY_TIME = 7 * 24 * 60 * 60 * 1000 // 7天的毫秒数

/**
 * 检查 localStorage 是否可用
 */
function isStorageAvailable() {
  try {
    const test = '__storage_test__'
    localStorage.setItem(test, test)
    localStorage.removeItem(test)
    return true
  } catch (e) {
    return false
  }
}

/**
 * 保存测试进度
 * @param {Object} progressData - 进度数据对象
 * @param {Object} progressData.answers - 答案对象 { 0: 5, 1: 3, ... }
 * @param {Number} progressData.currentQuestionIndex - 当前题目索引
 * @param {Number} progressData.questionsCount - 题目总数
 * @returns {Boolean} 是否保存成功
 */
export function saveProgress({ answers, currentQuestionIndex, questionsCount }) {
  if (!isStorageAvailable()) {
    console.warn('localStorage 不可用，无法保存进度')
    return false
  }

  try {
    const progressData = {
      answers: { ...answers }, // 深拷贝避免引用问题
      currentQuestionIndex,
      questionsCount,
      timestamp: Date.now()
    }

    localStorage.setItem(STORAGE_KEY, JSON.stringify(progressData))
    return true
  } catch (error) {
    // 处理存储空间不足等错误
    if (error.name === 'QuotaExceededError') {
      console.warn('存储空间不足，无法保存进度')
    } else {
      console.error('保存进度失败:', error)
    }
    return false
  }
}

/**
 * 加载测试进度
 * @returns {Object|null} 进度数据对象，如果不存在或无效则返回 null
 */
export function loadProgress() {
  if (!isStorageAvailable()) {
    return null
  }

  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) {
      return null
    }

    const progressData = JSON.parse(stored)

    // 验证数据有效性
    if (!isProgressValid(progressData)) {
      // 数据无效，清除它
      clearProgress()
      return null
    }

    return progressData
  } catch (error) {
    console.error('加载进度失败:', error)
    // 数据解析失败，清除损坏的数据
    clearProgress()
    return null
  }
}

/**
 * 验证进度数据是否有效
 * @param {Object} progressData - 进度数据对象
 * @returns {Boolean} 是否有效
 */
function isProgressValid(progressData) {
  // 检查必要字段
  if (!progressData || typeof progressData !== 'object') {
    return false
  }

  if (typeof progressData.answers !== 'object' || !progressData.answers) {
    return false
  }

  if (typeof progressData.currentQuestionIndex !== 'number' || 
      progressData.currentQuestionIndex < 0) {
    return false
  }

  if (typeof progressData.questionsCount !== 'number' || 
      progressData.questionsCount <= 0) {
    return false
  }

  if (typeof progressData.timestamp !== 'number' || 
      progressData.timestamp <= 0) {
    return false
  }

  // 检查是否过期（7天）
  const now = Date.now()
  if (now - progressData.timestamp > EXPIRY_TIME) {
    console.log('进度数据已过期，自动清除')
    return false
  }

  // 验证答案格式：所有值应该是 1-5 之间的数字
  for (const key in progressData.answers) {
    const answer = progressData.answers[key]
    if (typeof answer !== 'number' || answer < 1 || answer > 5) {
      return false
    }
    // 验证索引是否有效
    const index = parseInt(key)
    if (isNaN(index) || index < 0 || index >= progressData.questionsCount) {
      return false
    }
  }

  // 验证当前题目索引是否在有效范围内
  if (progressData.currentQuestionIndex >= progressData.questionsCount) {
    return false
  }

  return true
}

/**
 * 清除测试进度
 * @returns {Boolean} 是否清除成功
 */
export function clearProgress() {
  if (!isStorageAvailable()) {
    return false
  }

  try {
    localStorage.removeItem(STORAGE_KEY)
    return true
  } catch (error) {
    console.error('清除进度失败:', error)
    return false
  }
}

/**
 * 检查是否有有效的进度数据
 * @returns {Boolean} 是否有有效进度
 */
export function hasValidProgress() {
  const progress = loadProgress()
  return progress !== null
}

/**
 * 保存测试结果
 * @param {Object} results - 测试结果对象 { A: 15, B: 20, ... }
 * @returns {Boolean} 是否保存成功
 */
export function saveTestResults(results) {
  if (!isStorageAvailable()) {
    console.warn('localStorage 不可用，无法保存测试结果')
    return false
  }

  try {
    const resultsData = {
      results: { ...results }, // 深拷贝避免引用问题
      timestamp: Date.now()
    }

    localStorage.setItem(RESULTS_STORAGE_KEY, JSON.stringify(resultsData))
    return true
  } catch (error) {
    // 处理存储空间不足等错误
    if (error.name === 'QuotaExceededError') {
      console.warn('存储空间不足，无法保存测试结果')
    } else {
      console.error('保存测试结果失败:', error)
    }
    return false
  }
}

/**
 * 加载测试结果
 * @returns {Object|null} 测试结果对象，如果不存在或无效则返回 null
 */
export function loadTestResults() {
  console.log('从 localStorage 加载测试结果')
  if (!isStorageAvailable()) {
    return null
  }

  try {
    const stored = localStorage.getItem(RESULTS_STORAGE_KEY)
    if (!stored) {
      return null
    }

    const resultsData = JSON.parse(stored)

    // 验证数据有效性
    if (!isResultsValid(resultsData)) {
      // 数据无效，清除它
      clearTestResults()
      return null
    }
    console.log('加载测试结果成功:', resultsData.results)
    return resultsData.results
  } catch (error) {
    console.error('加载测试结果失败:', error)
    // 数据解析失败，清除损坏的数据
    clearTestResults()
    return null
  }
}

/**
 * 验证测试结果数据是否有效
 * @param {Object} resultsData - 测试结果数据对象
 * @returns {Boolean} 是否有效
 */
function isResultsValid(resultsData) {
  // 检查必要字段
  if (!resultsData || typeof resultsData !== 'object') {
    return false
  }

  if (typeof resultsData.results !== 'object' || !resultsData.results) {
    return false
  }

  if (typeof resultsData.timestamp !== 'number' || 
      resultsData.timestamp <= 0) {
    return false
  }

  // 检查是否过期（7天）
  const now = Date.now()
  if (now - resultsData.timestamp > EXPIRY_TIME) {
    console.log('测试结果数据已过期，自动清除')
    return false
  }

  // 验证结果格式：所有值应该是数字，且范围合理（0-20，因为每题最高5分，每个天赋类型有4题）
  const validCategories = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J']
  
  // 验证是否包含所有必要的天赋类型
  for (const category of validCategories) {
    if (!(category in resultsData.results)) {
      return false
    }
  }
  
  // 验证值的格式和范围
  for (const key in resultsData.results) {
    const value = resultsData.results[key]
    // 值必须是数字，且范围合理（0-20）
    if (typeof value !== 'number' || value < 0 || value > 20) {
      return false
    }
    // 确保键是有效的天赋类型
    if (!validCategories.includes(key)) {
      return false
    }
  }

  return true
}

/**
 * 清除测试结果
 * @returns {Boolean} 是否清除成功
 */
export function clearTestResults() {
  if (!isStorageAvailable()) {
    return false
  }

  try {
    localStorage.removeItem(RESULTS_STORAGE_KEY)
    return true
  } catch (error) {
    console.error('清除测试结果失败:', error)
    return false
  }
}

